import React, { useMemo, useState } from 'react';
import { FaTimes, FaFolderOpen, FaExclamationTriangle } from 'react-icons/fa';
import { REQUIRED_CV_DOCUMENT_TYPES } from '../../utils/cvDocuments';
import pageStyles from './EmployeeManagementPage.module.css';
import styles from './MissingDocumentsModal.module.css';

/**
 * Lists active employees with missing required CV/GCP/IATA documents.
 * `onOpenFiles(employee)` opens that employee's file modal on the page; pass null to hide it.
 */
const MissingDocumentsModal = ({ rows, loading, error, onClose, onOpenFiles }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const countsByType = useMemo(() =>
    REQUIRED_CV_DOCUMENT_TYPES.map(type => ({
      ...type,
      count: rows.filter(r => r.missing.some(m => m.key === type.key)).length
    })), [rows]);

  const term = searchTerm.trim().toLocaleLowerCase('tr');
  const visibleRows = term
    ? rows.filter(({ employee }) =>
        `${employee.eName || ''} ${employee.eSurname || ''}`.toLocaleLowerCase('tr').includes(term))
    : rows;

  return (
    <div className={pageStyles.modalOverlay} onClick={onClose}>
      <div className={`${pageStyles.pendingLeavesModal} ${styles.modal}`} onClick={(e) => e.stopPropagation()}>
        <div className={pageStyles.modalHeader}>
          <h3 className={pageStyles.modalTitle}>
            <FaExclamationTriangle className={styles.titleIcon} /> Eksik Belgesi Olan Çalışanlar ({rows.length})
          </h3>
          <button className={pageStyles.closeButton} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
        </div>

        <div className={pageStyles.modalBody}>
          <p className={styles.hint}>
            Aktif çalışanlar için zorunlu CV/GCP/IATA belgeleri kontrol edilir. Belgeleri çalışan kendi profilinden yükler.
          </p>

          <div className={styles.summary}>
            {countsByType.map(type => (
              <span key={type.key} className={styles.summaryItem}>
                {type.label}: <strong>{type.count}</strong>
              </span>
            ))}
          </div>

          <input
            type="text"
            className={styles.search}
            placeholder="Çalışan ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {loading ? (
            <div className={pageStyles.emptyState}><p>Yükleniyor...</p></div>
          ) : error ? (
            <div className={pageStyles.emptyState}><p>{error}</p></div>
          ) : visibleRows.length === 0 ? (
            <div className={pageStyles.emptyState}>
              <p>{term ? 'Aramaya uygun çalışan bulunamadı.' : 'Eksik belgesi olan çalışan bulunmamaktadır.'}</p>
            </div>
          ) : (
            <ul className={styles.list}>
              {visibleRows.map(({ employee, missing }) => (
                <li key={employee.id} className={styles.row}>
                  <div className={styles.info}>
                    <span className={styles.name}>{employee.eName} {employee.eSurname}</span>
                    <div className={styles.chips}>
                      {missing.map(m => (
                        <span key={m.key} className={styles.chip}>{m.label}</span>
                      ))}
                    </div>
                  </div>
                  {onOpenFiles && (
                    <button
                      type="button"
                      className={styles.openButton}
                      onClick={() => onOpenFiles(employee)}
                      title="Çalışanın dosyalarını aç"
                    >
                      <FaFolderOpen /> Dosyalar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={pageStyles.modalActions}>
          <button className={`${pageStyles.btn} ${pageStyles.btnCancel}`} onClick={onClose}>
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissingDocumentsModal;
